import * as React from 'react'
import { CircleIcon } from 'lucide-react'
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group'
import { Radio as RadioPrimitive } from '@base-ui/react/radio'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// RadioGroup — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Base UI splits radio into two imports:
//   @base-ui/react/radio-group → RadioGroup (root container)
//   @base-ui/react/radio       → Radio.Root + Radio.Indicator
//
// Data attribute changes:
//   data-state="checked"   → data-checked
//   data-state="unchecked" → data-unchecked
//
// Radix uses RadioGroup.Root + RadioGroup.Item + RadioGroup.Indicator
// Base UI uses RadioGroup + Radio.Root + Radio.Indicator
// ---------------------------------------------------------------------------

function RadioGroup({
  className,
  orientation = 'vertical',
  ...props
}: Omit<React.ComponentProps<typeof RadioGroupPrimitive>, 'render'> & {
  /** Visual layout direction — Base UI RadioGroup does not have a native orientation prop */
  orientation?: 'horizontal' | 'vertical'
}) {
  return (
    <RadioGroupPrimitive
      data-slot="radio-group"
      data-orientation={orientation}
      className={cn('grid gap-3', orientation === 'horizontal' && 'flex flex-wrap gap-x-6 gap-y-2', className)}
      {...props}
    />
  )
}

// DS Radio item spec:
//   Control: size-4 (16px), rounded-full, border-input at rest
//   Selected: inner filled circle (size-2) at fill-primary; outer border becomes border-primary
//   Hover:   subtle border tint
//   Focused: ring-[3px] ring-ring/50
//   Error:   border-destructive, ring-destructive/20
//   Disabled: opacity-50, cursor-not-allowed
function RadioGroupItem({ className, ...props }: Omit<React.ComponentProps<typeof RadioPrimitive.Root>, 'render'>) {
  return (
    <RadioPrimitive.Root
      data-slot="radio-group-item"
      className={cn(
        'aspect-square size-4 shrink-0 rounded-full border border-input text-primary shadow-xs transition-[color,box-shadow] outline-none',
        'hover:border-ring/60',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        // Base UI: data-[checked] replaces Radix data-[state=checked]
        'data-[checked]:border-primary',
        'dark:bg-input/30',
        className,
      )}
      {...props}
    >
      <RadioPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
        keepMounted
      >
        <CircleIcon className="absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 fill-primary" />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  )
}

/** Per-option supplementary description text (DS pattern) */
function RadioGroupItemDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="radio-group-item-description"
      className={cn('text-xs text-muted-foreground leading-snug', className)}
      {...props}
    />
  )
}

export { RadioGroup, RadioGroupItem, RadioGroupItemDescription }
